import { Card, CardContent } from "../../components/ui/card";
import { Home, TrendingUp, Users, Target, Award, Heart } from "lucide-react";

const About = () => {
  return (
    <div className="min-h-screen" style={{ backgroundColor: "#faf9f5" }}>
      <div className="container mx-auto max-w-6xl px-4 sm:px-6 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold mb-4 bg-gradient-to-r from-primary via-orange-500 to-orange-600 bg-clip-text text-transparent">
            About Koflip
          </h1>
          <p className="text-lg md:text-xl text-muted-foreground max-w-3xl mx-auto">
            Revolutionizing real estate by connecting homeowners and investors
            for mutual benefit.
          </p>
        </div>

        {/* Founder's Story Section */}
        <Card className="mb-8 bg-white shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4 mb-4">
              <div
                className="h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background:
                    "linear-gradient(135deg, #c96442 0%, #ea580c 100%)",
                }}
              >
                <Heart className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-3 text-gray-900">
                  Our Story
                </h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  <p>
                    <strong className="text-gray-900">Steve Azoulay</strong> has
                    spent more than twenty years shaping Miami's high-end
                    construction landscape through his company,{" "}
                    <strong className="text-gray-900">
                      Yaz Enterprises USA
                    </strong>
                    . Known for managing luxury renovations and new builds,
                    Steve earned the trust of clients by combining creative
                    design with disciplined execution. His projects across South
                    Florida reflect a commitment to quality, transparency, and
                    lasting value.
                  </p>
                  <p>
                    Over time, Steve began to see a recurring issue. Many
                    homeowners owned valuable properties but couldn't access the
                    funds needed to unlock their potential. Banks were slow,
                    financing options were limited, and too often, great
                    opportunities to renovate or rebuild were lost.
                  </p>
                  <p>
                    That realization sparked the idea for{" "}
                    <strong className="text-gray-900">Koflip</strong> — a model
                    that merges construction expertise with smart, flexible
                    financing. Koflip helps homeowners renovate or rebuild
                    without selling their property, while offering investors a
                    safe, transparent way to participate in renovation-backed
                    projects.
                  </p>
                  <p>
                    Powered by technology, clear contracts, and a results-driven
                    structure, Koflip turns untapped property value into shared
                    profit. For Steve, it's more than just a business — it's the
                    evolution of two decades of experience in construction,
                    project management, and trust-building, now transformed into
                    a platform that empowers both homeowners and investors to
                    grow together.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Mission Section */}
        <Card className="mb-8 bg-white shadow-lg">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4 mb-4">
              <div
                className="h-12 w-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{
                  background:
                    "linear-gradient(135deg, #c96442 0%, #ea580c 100%)",
                }}
              >
                <Target className="h-6 w-6 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold mb-3 text-gray-900">
                  Our Mission
                </h2>
                <p className="text-muted-foreground leading-relaxed">
                  At Koflip, we believe homeowners shouldn't have to compromise
                  their ownership to unlock their property's potential. Our
                  mission is to empower homeowners to renovate and increase
                  their property value without upfront costs, while providing
                  investors with secure, token-based real estate opportunities.
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* What We Do */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">
            What We Do
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* For Homeowners */}
            <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div
                  className="h-14 w-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{
                    background:
                      "linear-gradient(135deg, #c96442 0%, #ea580c 100%)",
                  }}
                >
                  <Home className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-center text-primary">
                  For Homeowners
                </h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Get up to 2 years rent upfront without selling</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>We cover all renovation costs</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Guaranteed 15% return on property value</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Retain full ownership throughout</span>
                  </li>
                </ul>
              </CardContent>
            </Card>

            {/* For Investors */}
            <Card className="bg-white shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="pt-6">
                <div
                  className="h-14 w-14 rounded-xl flex items-center justify-center mx-auto mb-4"
                  style={{
                    background:
                      "linear-gradient(135deg, #c96442 0%, #ea580c 100%)",
                  }}
                >
                  <TrendingUp className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-bold mb-3 text-center text-primary">
                  For Investors
                </h3>
                <ul className="space-y-2 text-muted-foreground">
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Earn 14-18% annual returns</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Token-based investment model</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Early exit options available</span>
                  </li>
                  <li className="flex items-start">
                    <span className="text-primary mr-2">•</span>
                    <span>Diversified real estate portfolio access</span>
                  </li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* Values Section */}
        <div className="mb-12">
          <h2 className="text-3xl font-bold text-center mb-8 text-gray-900">
            Our Values
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <Card className="bg-white shadow-lg text-center">
              <CardContent className="pt-6">
                <div
                  className="h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: "#f0f0ff" }}
                >
                  <Users className="h-6 w-6 text-purple-600" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">
                  Community First
                </h3>
                <p className="text-sm text-muted-foreground">
                  Building trust and transparency between homeowners and
                  investors.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg text-center">
              <CardContent className="pt-6">
                <div
                  className="h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: "#fff5f0" }}
                >
                  <Award className="h-6 w-6 text-orange-600" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">
                  Excellence
                </h3>
                <p className="text-sm text-muted-foreground">
                  Delivering high-quality renovations and reliable financial
                  returns.
                </p>
              </CardContent>
            </Card>

            <Card className="bg-white shadow-lg text-center">
              <CardContent className="pt-6">
                <div
                  className="h-12 w-12 rounded-full flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: "#f0fff4" }}
                >
                  <Heart className="h-6 w-6 text-green-600" />
                </div>
                <h3 className="text-lg font-bold mb-2 text-gray-900">
                  Integrity
                </h3>
                <p className="text-sm text-muted-foreground">
                  Operating with honesty and fairness in every transaction.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>

        {/* How It Works */}
        <Card className="bg-white shadow-lg mb-8">
          <CardContent className="pt-6">
            <h2 className="text-2xl font-bold mb-6 text-center text-gray-900">
              How Koflip Works
            </h2>
            <div className="space-y-4">
              <div className="flex items-start gap-4">
                <div
                  className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold"
                  style={{
                    background:
                      "linear-gradient(135deg, #c96442 0%, #ea580c 100%)",
                  }}
                >
                  1
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Property Assessment
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Homeowners submit their property details through our
                    calculator for evaluation.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div
                  className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold"
                  style={{
                    background:
                      "linear-gradient(135deg, #c96442 0%, #ea580c 100%)",
                  }}
                >
                  2
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Renovation Planning
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    We design and plan renovations to maximize property value
                    and appeal.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div
                  className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold"
                  style={{
                    background:
                      "linear-gradient(135deg, #c96442 0%, #ea580c 100%)",
                  }}
                >
                  3
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Investor Matching
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    Token-based investments are offered to our investor network
                    for funding.
                  </p>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div
                  className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0 text-white font-bold"
                  style={{
                    background:
                      "linear-gradient(135deg, #c96442 0%, #ea580c 100%)",
                  }}
                >
                  4
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900 mb-1">
                    Property Sale & Distribution
                  </h3>
                  <p className="text-muted-foreground text-sm">
                    After renovation and sale, profits are distributed according
                    to agreed terms.
                  </p>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA Section */}
        <div className="text-center">
          <Card className="bg-white shadow-lg">
            <CardContent className="pt-6 pb-6">
              <h2 className="text-2xl font-bold mb-3 text-gray-900">
                Ready to Get Started?
              </h2>
              <p className="text-muted-foreground mb-6">
                Whether you're a homeowner looking to unlock your property's
                value or an investor seeking high returns, we're here to help.
              </p>
              <div className="flex gap-4 justify-center flex-wrap">
                <a
                  href="/search"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold text-white shadow-lg hover:shadow-xl transition-all"
                  style={{
                    background:
                      "linear-gradient(90deg, #c96442 0%, #ea580c 100%)",
                  }}
                >
                  Try Our Calculator
                </a>
                <a
                  href="mailto:support@koflip.com"
                  className="inline-flex items-center justify-center px-6 py-3 rounded-xl font-semibold border-2 transition-all hover:bg-gray-50"
                  style={{ borderColor: "#c96442", color: "#c96442" }}
                >
                  Contact Us
                </a>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default About;
